import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { LayoutDashboard, Users, Store, Settings2, Gauge, Calculator, Wallet, Lock, ScrollText, Network, Menu, X, TrendingUp, Banknote, Receipt, UserSquare2, Mail, LogOut, FileText, BookOpen, LifeBuoy, Percent, CalendarDays, Filter } from "lucide-react";
import { BrandingProvider, useBranding } from "@/lib/branding";
import { AuthProvider, useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Programs from "@/pages/Programs";
import VolumeInput from "@/pages/VolumeInput";
import Calculations from "@/pages/Calculations";
import Payouts from "@/pages/Payouts";
import Partners from "@/pages/Partners";
import Merchants from "@/pages/Merchants";
import Gateways from "@/pages/Gateways";
import AuditLog from "@/pages/AuditLog";
import PeriodClose from "@/pages/PeriodClose";
import Settings from "@/pages/Settings";
import InternalMargin from "@/pages/InternalMargin";
import PGEarnings from "@/pages/PGEarnings";
import MonthlyAccounting from "@/pages/MonthlyAccounting";
import People from "@/pages/People";
import RateCalendar from "@/pages/RateCalendar";
import {
  PartnerDashboard, PartnerPayouts, PartnerStatements, PartnerRateCards,
  PartnerApiDocs,
  MerchantDashboard, MerchantVolumes, MerchantCommercials,
  MerchantApiDocs, MerchantSupport, PartnerSupport,
} from "@/pages/Portal";
import Holidays from "@/pages/Holidays";
import Leads from "@/pages/Leads";
import { PartnerOnboarding, MerchantNewMid } from "@/pages/Onboarding";
import ForceChangePassword from "@/pages/ForceChangePassword";
import { DialogHost } from "@/lib/dialogs";

const ADMIN_NAV = [
  { section: "Overview" },
  { to: "/dashboard", label: "Summary", icon: LayoutDashboard, testid: "nav-dashboard" },

  { section: "Directory" },
  { to: "/merchants", label: "Merchants", icon: Store, testid: "nav-merchants" },
  { to: "/partners", label: "Partners", icon: Users, testid: "nav-partners" },
  { to: "/gateways", label: "Payment Gateways", icon: Network, testid: "nav-gateways" },
  { to: "/leads", label: "Leads", icon: Filter, testid: "nav-leads" },
  { to: "/people", label: "People", icon: UserSquare2, testid: "nav-people" },

  { section: "Operations" },
  { to: "/volume", label: "Volume Input", icon: Gauge, testid: "nav-volume" },
  { to: "/payouts", label: "Partner Payouts", icon: Wallet, testid: "nav-payouts" },
  { to: "/internal-margin", label: "Internal Margin", icon: TrendingUp, testid: "nav-internal-margin" },
  { to: "/pg-earnings", label: "PG Earnings", icon: Banknote, testid: "nav-pg-earnings" },
  { to: "/monthly-accounting", label: "Monthly Accounting", icon: Receipt, testid: "nav-monthly-accounting" },

  { section: "Admin" },
  { to: "/period-close", label: "Period Close", icon: Lock, testid: "nav-period-close" },
  { to: "/holidays", label: "Settlement Holidays", icon: CalendarDays, testid: "nav-holidays" },
  { to: "/rate-calendar", label: "Rate Calendar", icon: Percent, testid: "nav-rate-calendar" },
  { to: "/settings", label: "Settings", icon: Mail, testid: "nav-settings" },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, testid: "nav-audit" },
];

const PARTNER_NAV = [
  { section: "Your Portal" },
  { to: "/portal/partner",            label: "Dashboard",   icon: LayoutDashboard, testid: "nav-partner-dashboard" },
  { to: "/portal/partner/onboarding", label: "Onboarding",  icon: Filter,          testid: "nav-partner-onboarding" },
  { to: "/portal/partner/payouts",    label: "Payouts",     icon: Wallet,          testid: "nav-partner-payouts" },
  { to: "/portal/partner/statements", label: "Statements",  icon: FileText,        testid: "nav-partner-statements" },
  { to: "/portal/partner/rate-cards", label: "Rate Cards",  icon: Percent,         testid: "nav-partner-rate-cards" },

  { section: "Resources" },
  { to: "/portal/partner/holidays",   label: "Settlement Holidays", icon: CalendarDays, testid: "nav-partner-holidays" },
  { to: "/portal/partner/api-docs",   label: "API Docs",            icon: BookOpen,     testid: "nav-partner-api-docs" },
  { to: "/portal/partner/support",    label: "Support",             icon: LifeBuoy,     testid: "nav-partner-support" },
];

const MERCHANT_NAV = [
  { section: "Your Portal" },
  { to: "/portal/merchant",             label: "Dashboard",     icon: LayoutDashboard, testid: "nav-merchant-dashboard" },
  { to: "/portal/merchant/volumes",     label: "Volumes",       icon: TrendingUp,      testid: "nav-merchant-volumes" },
  { to: "/portal/merchant/commercials", label: "Commercials",   icon: Receipt,         testid: "nav-merchant-commercials" },
  { to: "/portal/merchant/mid-requests",label: "MID Requests",  icon: Filter,          testid: "nav-merchant-mid-requests" },

  { section: "Resources" },
  { to: "/portal/merchant/holidays",    label: "Settlement Holidays", icon: CalendarDays, testid: "nav-merchant-holidays" },
  { to: "/portal/merchant/api-docs",    label: "API Docs",      icon: BookOpen,        testid: "nav-merchant-api-docs" },
  { to: "/portal/merchant/support",     label: "Support",       icon: LifeBuoy,        testid: "nav-merchant-support" },
];

function navFor(role) {
  if (role === "partner") return PARTNER_NAV;
  if (role === "merchant") return MERCHANT_NAV;
  return ADMIN_NAV;
}

function BrandMark({ logo, label, dotIdx, suffix, suffixClass }) {
  // Renders the logo + app-name combo. Splits the label so we can apply
  // a colored dot between the first and remaining words (matches existing aesthetic).
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-header">
      <div className="h-10 w-10 rounded-sm bg-white grid place-items-center shrink-0 overflow-hidden">
        {logo
          ? <img src={logo} alt={label} className="h-8 w-8 object-contain" />
          : <img src="/bizassist-logo.png?v=2" alt={label} className="h-8 w-8 object-contain" />}
      </div>
      <div className="leading-none">
        <div className="font-display text-[17px] tracking-tight text-white">
          {label.slice(0, dotIdx)}<span className="text-[var(--accent)]">.</span>
        </div>
        <div className={`text-[10px] uppercase tracking-[0.18em] text-white/50 mt-1.5 ${suffixClass || ""}`}>{suffix}</div>
      </div>
    </div>
  );
}

function homePathFor(role) {
  if (role === "partner") return "/portal/partner";
  if (role === "merchant") return "/portal/merchant";
  return "/dashboard";
}

function Sidebar({ onNavigate }) {
  const { app_name, logo_data_url } = useBranding();
  const { user, logout } = useAuth();
  const items = navFor(user?.role);
  const trimmed = (app_name || "BizAssist Online").trim();
  const splitAt = trimmed.indexOf(" ");
  const head = splitAt === -1 ? trimmed : trimmed.slice(0, splitAt);
  const tail = splitAt === -1 ? "" : trimmed.slice(splitAt + 1);
  const suffix = user?.role === "partner" ? "Partner Portal"
               : user?.role === "merchant" ? "Merchant Portal"
               : (tail || "App");
  return (
    <>
      <div className="px-5 pt-6 pb-5">
        <Link
          to={homePathFor(user?.role)}
          onClick={onNavigate}
          data-testid="brand-home-link"
          className="block rounded-sm hover:opacity-90 transition-opacity"
          aria-label="Go to home"
        >
          <BrandMark logo={logo_data_url} label={head} dotIdx={head.length} suffix={suffix} />
        </Link>
      </div>
      <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
        {items.map((item, idx) => {
          if (item.section) {
            return (
              <div key={`sec-${idx}`} className={`px-4 pt-3 pb-1.5 text-[9.5px] font-semibold uppercase tracking-[0.22em] text-white/35 ${idx === 0 ? "" : "mt-1"}`}>
                {item.section}
              </div>
            );
          }
          const Icon = item.icon;
          return (
            <NavLink key={item.to} to={item.to} data-testid={item.testid} onClick={onNavigate}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 text-[13.5px] font-medium rounded-sm ${isActive ? "active" : ""}`}>
              <Icon size={16} strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-white/5">
        {user && (
          <>
            <div className="px-4 py-3">
              <div className="min-w-0">
                <div className="text-[12px] text-white/85 truncate">{user.email}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">{user.role}{user.has_2fa ? " · 2FA" : ""}</div>
              </div>
            </div>
            <button
              data-testid="sidebar-logout"
              onClick={async () => { await logout(); onNavigate?.(); }}
              className="mx-3 mb-3 h-9 w-[calc(100%-1.5rem)] inline-flex items-center justify-center gap-2 rounded-sm text-[12.5px] font-medium text-white/85 bg-white/5 hover:bg-white/10 hover:text-white border border-white/10 transition-colors"
            >
              <LogOut size={14} strokeWidth={1.75} />
              <span>Logout</span>
            </button>
          </>
        )}
        <div className="px-6 py-3 text-[11px] text-white/40">v2.0 · INR · All periods</div>
      </div>
    </>
  );
}

function Shell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const currentLabel = navFor(user?.role).find((n) => n.to && location.pathname.startsWith(n.to))?.label || "";
  const { app_name, logo_data_url } = useBranding();
  const trimmed = (app_name || "BizAssist Online").trim();
  const splitAt = trimmed.indexOf(" ");
  const head = splitAt === -1 ? trimmed : trimmed.slice(0, splitAt);
  const isDemo = !!user?.demo;
  const isAdmin = user?.role === "admin" || user?.role === "demo";

  // Admin-only: load holiday coverage once per session and surface a slim
  // banner if the calendar is running thin (warning) or stale (urgent).
  // Partner/Merchant portals never see this — they only consume the
  // calendar, they don't manage it. Dismissed banners stay hidden for the
  // session via in-memory state (refresh re-evaluates).
  const [coverage, setCoverage] = useState(null);
  const [coverageDismissed, setCoverageDismissed] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    import("@/lib/api").then(({ api }) => {
      api.get("/holidays/coverage")
        .then((r) => mounted && setCoverage(r.data))
        .catch(() => { /* silently ignore — non-critical */ });
    });
    return () => { mounted = false; };
  }, [isAdmin]);
  const showCoverageBanner = isAdmin
    && coverage
    && coverage.alert_level !== "ok"
    && !coverageDismissed
    && !location.pathname.startsWith("/holidays"); // already shown in-page

  return (
    <div className="min-h-screen flex" data-testid="app-shell" data-demo={isDemo ? "true" : "false"}>
      {isDemo && (
        <div
          className="fixed top-0 left-0 right-0 z-50 h-8 bg-amber-400 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-900 flex items-center justify-center gap-2 border-b border-amber-500"
          data-testid="demo-banner"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-amber-900 animate-pulse" />
          Demo sandbox — sample data · not your real numbers
        </div>
      )}
      {/* Desktop sidebar */}
      <aside className={`side-nav w-60 shrink-0 hidden lg:flex flex-col ${isDemo ? "pt-8" : ""}`} data-testid="sidebar">
        <Sidebar />
      </aside>

      {/* Mobile top bar */}
      <div
        className={`lg:hidden fixed left-0 right-0 h-12 bg-[var(--ink)] z-30 flex items-center justify-between px-3 border-b border-white/5 ${isDemo ? "top-8" : "top-0"}`}
        data-testid="mobile-topbar"
      >
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(true)}
          className="h-9 w-9 grid place-items-center rounded-sm text-white hover:bg-white/10"
          aria-label="Open navigation"
        >
          <Menu size={20} strokeWidth={1.75} />
        </button>
        <Link
          to={homePathFor(user?.role)}
          className="flex items-center gap-2 min-w-0 hover:opacity-90 transition-opacity"
          data-testid="brand-home-link-mobile"
          aria-label="Go to home"
        >
          <div className="h-7 w-7 rounded-sm bg-white grid place-items-center overflow-hidden shrink-0">
            {logo_data_url
              ? <img src={logo_data_url} alt={head} className="h-5 w-5 object-contain" />
              : <img src="/bizassist-logo.png?v=2" alt={head} className="h-5 w-5 object-contain" />}
          </div>
          <div className="font-display text-[14px] text-white tracking-tight truncate">
            {head}<span className="text-[var(--accent)]">.</span>
          </div>
        </Link>
        <div className="text-[11px] text-white/60 pr-1 max-w-[40%] truncate">{currentLabel}</div>
      </div>

      {/* Mobile drawer + backdrop */}
      {mobileOpen && (
        <>
          <button
            aria-label="Close navigation backdrop"
            data-testid="mobile-drawer-backdrop"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
          />
          <aside
            data-testid="mobile-drawer"
            className="side-nav lg:hidden fixed inset-y-0 left-0 w-64 flex flex-col z-50 shadow-2xl"
          >
            <button
              data-testid="mobile-drawer-close"
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-sm text-white/80 hover:bg-white/10"
              aria-label="Close navigation"
            >
              <X size={18} />
            </button>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <main className={`flex-1 min-w-0 pt-12 lg:pt-0 ${isDemo ? "lg:pt-8" : ""}`}>
        {showCoverageBanner && (
          <div
            data-testid={`global-holiday-banner-${coverage.alert_level}`}
            className={`flex items-center gap-3 px-4 sm:px-10 py-2.5 border-b text-[12.5px] ${
              coverage.alert_level === "urgent"
                ? "bg-red-50 border-red-200 text-red-900"
                : "bg-amber-50 border-amber-200 text-amber-900"
            }`}
          >
            <span className="font-medium shrink-0">
              {coverage.alert_level === "urgent" ? "Calendar action required" : "Calendar running low"}
            </span>
            <span className="opacity-80 truncate">{coverage.message}</span>
            <Link
              to="/holidays"
              className="ml-auto shrink-0 underline underline-offset-2 hover:opacity-80"
              data-testid="global-holiday-banner-link"
            >
              Manage calendar →
            </Link>
            <button
              type="button"
              onClick={() => setCoverageDismissed(true)}
              className="shrink-0 opacity-60 hover:opacity-100"
              aria-label="Dismiss"
              data-testid="global-holiday-banner-dismiss"
            >
              <X size={14} />
            </button>
          </div>
        )}
        {children}
      </main>
      <Toaster position="top-right" richColors closeButton duration={4000} />
    </div>
  );
}

function ProtectedApp() {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen grid place-items-center text-[var(--muted)] text-sm" data-testid="auth-loading">Loading…</div>;
  }
  if (!user) {
    return (
      <>
        <Login />
        {/* Login lives outside <Shell>, so it doesn't inherit the protected
            app's <Toaster>. Render a dedicated one here so error toasts
            (wrong creds, role mismatch, etc.) actually surface. */}
        <Toaster position="top-right" richColors closeButton duration={4000} />
      </>
    );
  }
  // First-login gate — partner/merchant users created with a temp password
  // have to set their own before they can navigate anywhere.
  if (user.must_change_password) {
    return (
      <>
        <ForceChangePassword />
        <Toaster position="top-right" richColors closeButton duration={4000} />
      </>
    );
  }
  // Partner-scoped routes
  if (user.role === "partner") {
    return (
      <Shell>
        <Routes>
          <Route path="/" element={<Navigate to="/portal/partner" replace />} />
          <Route path="/portal/partner" element={<PartnerDashboard />} />
          <Route path="/portal/partner/payouts" element={<PartnerPayouts />} />
          <Route path="/portal/partner/statements" element={<PartnerStatements />} />
          <Route path="/portal/partner/rate-cards" element={<PartnerRateCards />} />
          <Route path="/portal/partner/holidays" element={<Holidays />} />
          <Route path="/portal/partner/api-docs" element={<PartnerApiDocs />} />
          <Route path="/portal/partner/support" element={<PartnerSupport />} />
          <Route path="/portal/partner/onboarding" element={<PartnerOnboarding />} />
          <Route path="*" element={<Navigate to="/portal/partner" replace />} />
        </Routes>
      </Shell>
    );
  }
  // Merchant-scoped routes
  if (user.role === "merchant") {
    return (
      <Shell>
        <Routes>
          <Route path="/" element={<Navigate to="/portal/merchant" replace />} />
          <Route path="/portal/merchant" element={<MerchantDashboard />} />
          <Route path="/portal/merchant/volumes" element={<MerchantVolumes />} />
          <Route path="/portal/merchant/commercials" element={<MerchantCommercials />} />
          <Route path="/portal/merchant/api-docs" element={<MerchantApiDocs />} />
          <Route path="/portal/merchant/support" element={<MerchantSupport />} />
          <Route path="/portal/merchant/holidays" element={<Holidays />} />
          <Route path="/portal/merchant/mid-requests" element={<MerchantNewMid />} />
          <Route path="*" element={<Navigate to="/portal/merchant" replace />} />
        </Routes>
      </Shell>
    );
  }
  // Admin (default)
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/programs" element={<Programs />} />
        <Route path="/volume" element={<VolumeInput />} />
        <Route path="/calculations" element={<Calculations />} />
        <Route path="/payouts" element={<Payouts />} />
        <Route path="/internal-margin" element={<InternalMargin />} />
        <Route path="/pg-earnings" element={<PGEarnings />} />
        <Route path="/monthly-accounting" element={<MonthlyAccounting />} />
        <Route path="/people" element={<People />} />
        <Route path="/period-close" element={<PeriodClose />} />
        <Route path="/holidays" element={<Holidays />} />
        <Route path="/rate-calendar" element={<RateCalendar />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/gateways" element={<Gateways />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/merchants" element={<Merchants />} />
        <Route path="/audit" element={<AuditLog />} />
        {/* Catch /login and /reset-password when already authenticated → land on dashboard */}
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/reset-password" element={<Navigate to="/dashboard" replace />} />
        {/* Catch-all: any unknown URL (e.g. /portal/merchant after switching from
            a merchant session) lands the admin on the Summary page so they
            never see a blank screen. */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrandingProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Public, doesn't need auth */}
              <Route path="/reset-password" element={<><ResetPassword /><Toaster position="top-right" richColors closeButton duration={4000} /></>} />
              {/* Everything else flows through ProtectedApp which renders <Login /> if not authed */}
              <Route path="*" element={<ProtectedApp />} />
            </Routes>
            {/* Single mount point for in-app confirm()/prompt() replacements. */}
            <DialogHost />
          </AuthProvider>
        </BrowserRouter>
      </BrandingProvider>
    </div>
  );
}
